<template>
  <panel class="case-scenario" title="Scenario" color="blue-6">
    <div class="case-name-language-container">
      <div class="case-name-container">
        <label for="case-name-input">Scenario Name</label>
        <md-field :class="{'md-invalid': hasError('name')}">
          <md-input
            id="case-name-input"
            :value="state.caseName"
            :class="{'error': hasError('name')}"
            :maxlength="64"
            :disabled="readonly"
            @input="name => updateName(name)"
          />
          <span v-if="hasError('name')" class="md-error">{{ nameErrors[0].message }}</span>
        </md-field>
      </div>
      <div class="case-timer-container">
        <label>Scenario Timer</label>
        <help-tooltip v-if="hasError('timer')" type="error">
          Case timer must be greater than 0 seconds.
        </help-tooltip>
        <help-tooltip v-else type="info">
          Enter how long you want your case to run equal to or less than 2 hours.
        </help-tooltip>
        <div class="field-row">
          <md-field class="case-hours-field">
            <md-input
              ref="hoursInput"
              type="number"
              min="0"
              max="2"
              placeholder="hh"              
              :value="state.caseHours"            
              :disabled="readonly"
              @input="updateHours"
            />
          </md-field>
          <span class="field-colon">:</span>
          <md-field class="case-minutes-field">
            <md-input
              id="case-timer-minutes"
              ref="minutesInput"
              type="number"
              min="0"
              max="59"
              placeholder="mm"
              :value="state.caseMinutes"
              :disabled="readonly"
              @input="updateMinutes"
            />
          </md-field>
          <span class="field-colon">:</span>
          <md-field class="case-seconds-field">
            <md-input
              ref="secondsInput"
              type="number"
              min="0"
              max="59"
              placeholder="ss"
              :value="state.caseSeconds"
              :disabled="readonly"
              @input="updateSeconds"
            />
          </md-field>
        </div>
      </div>
    </div>

    <div class="md-layout case-avatar-environment-container">
      <div class="md-layout-item case-avatar-container">
        <label>Patient</label>
        <case-avatar-container
          :mounted="isActive"
          :readonly="readonly"
          :case-conf="caseConf"
          @update="update"
        />
      </div>

      <div class="md-layout-item case-environment-container">
        <label>Starting Scene</label>
        <case-environment-container
          :mounted="isActive"
          :case-conf="caseConf"
          location-key="_startLocation"
          :readonly="readonly"
          scene-select-event-name="scene_1_select"
          @update="update"
        />
      </div>

      <div class="md-layout-item case-environment-container">
        <help-tooltip v-if="endLocationWarnings.length" type="warning">
          <span v-for="warning in endLocationWarnings" :key="warning.message">
            {{ warning.message }}
            <br>
          </span>
        </help-tooltip>
        <label>Ending Scene</label>
        <help-tooltip
          color="blue-6"
          type="help"
        >
          If the ending scene is different from the starting scene, add it here.
        </help-tooltip>
        <case-environment-container
          :mounted="isActive"
          :case-conf="caseConf"
          location-key="_endLocation"
          :required="false"
          :readonly="readonly"
          scene-select-event-name="scene_2_select"
          @update="update"
        />
      </div>
    </div>
  </panel>
</template>

<script>
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';

import HelpTooltip from '~lib/common/components/HelpTooltip';

import Panel from '../../common/Panel';
import CaseAvatarContainer from '../CaseAvatar';
import CaseEnvironmentContainer from '../CaseEnvironment';

export default {
  name: 'case-scenario',
  components: {
    CaseAvatarContainer,
    CaseEnvironmentContainer,
    Panel,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    idToken: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
    },
    isActive: {
      type: Boolean,
    },
  },
  data() {
    return {
      state: {
        name: '',
      },
    };
  },
  computed: {
    nameErrors() {
      return this.$store.getters['validation/errorsByField'].name;
    },
    endLocationWarnings() {
      const warnings = this.$store.getters['validation/visibleWarningsByField']._endLocation || [];
      return uniqBy(warnings, 'message');
    },
  },
  created() {
    this.state.caseName = this.caseConf.name;
    this.state.caseHours = get(this.caseConf, 'timer.hours', '');
    this.state.caseMinutes = get(this.caseConf, 'timer.minutes', '15');
    this.state.caseSeconds = get(this.caseConf, 'timer.seconds', '');
  },
  methods: {
    updateName(name) {
      // We keep the untrimmed name in the state
      // to prevent the disruption of the user's typing
      this.state.caseName = name;
      const trimmedName = name.trim();
      this.update({ name: trimmedName });
    },
    updateHours(hours) {
      this.state.caseHours = hours;
      this.rangeLimit();
      this.updateTime();
    },
    updateMinutes(minutes) {
      this.state.caseMinutes = minutes;
      this.rangeLimit();
      this.updateTime();
    },
    updateSeconds(seconds) {
      this.state.caseSeconds = seconds;
      this.rangeLimit();
      this.updateTime();
    },
    rangeLimit() {
      const hours = Math.max(0, Math.min(2, this.state.caseHours))
      const minutes = hours === 2 ? 0 : Math.max(0, Math.min(59, this.state.caseMinutes));
      const seconds = hours === 2 ? 0 : Math.max(0, Math.min(59, this.state.caseSeconds));
      if (this.state.caseHours != hours) {
        this.state.caseHours = hours;
        this.$nextTick(() => {
          this.$refs.hoursInput.$el.value = hours;
        });
      }
      if (this.state.caseMinutes != minutes) {
        this.state.caseMinutes = minutes;
        this.$nextTick(() => {
          this.$refs.minutesInput.$el.value = minutes;
        });
      }
      if (this.state.caseSeconds != seconds) {
        this.state.caseSeconds = seconds;
        this.$nextTick(() => {
          this.$refs.secondsInput.$el.value = seconds;
        });
      }
    },
    updateTime() {
      const timer = {
        hours: this.state.caseHours,
        minutes: this.state.caseMinutes,
        seconds: this.state.caseSeconds,
      }
      this.update({ timer });
    },
    update(updates) {
      this.$emit('update', updates);
    },
    hasError(field) {
      return get(this.$store.getters['validation/errorsByField'], field);
    },
  },
};
</script>

<style scoped>
.case-avatar-container,
.case-environment-container {
  width: 33.3%;
}
.case-environment-container {
  padding-left: 50px;
}
.case-avatar-environment-container > * > label {
  line-height: 24px;
}

.case-timer-container {
  padding-left: 50px;
  width: 33.3%;
}

.field-row {
  display: flex;
  align-items: center;
}

 .field-colon {
   margin: 0 4px 0 2px;
 }

.case-link-container,
.case-name-language-container,
.case-avatar-environment-container {
  margin-top: 45px;
}

.case-name-language-container,
.case-avatar-environment-container {
  display: flex;
}

.case-name-container {
  width: 66.6%;
}

.case-hours-field,
.case-minutes-field,
.case-seconds-field {
  flex: 1;
  margin: 0;
}

.case-hours-field .md-input,
.case-minutes-field .md-input,
.case-seconds-field .md-input {
  text-align: center;
}

.case-name-container .md-field,
.case-timer-container .md-field {
  margin-top: 5px;
}

.help-tooltip.tooltip-icon {
  margin: 0 5px;
}
</style>
